import { useMemo } from 'react'

import type { TimeDisplayMode } from '@fit-editor/fit-core'

import { useFieldCell } from '@/components/data/field-cell-renderer'
import type { FitDataModel } from '@/components/data/use-fit-data-model'
import { useUnits } from '@/hooks/use-units'
import { discoverColumns, readColumn } from '@/lib/fit-data'
import type { ColumnDef } from '@/lib/fit-data'
import { formatFriendlyValue } from '@/lib/fit-data-format'
import { cn } from '@/lib/utils'

export interface MessageTableProps {
  model: FitDataModel
  messagesKey: string
  /** Accessible table caption (also shown as the visible heading). */
  caption: string
  /** Row header label prefix, e.g. "Lap" -> "Lap 1". */
  rowLabel: string
  preferredOrder: readonly string[]
  exclude?: readonly string[]
  source: 'current' | 'original'
  timeMode: TimeDisplayMode
  emptyText: string
}

/**
 * One row per message instance, one column per field that has data in
 * any instance. Used for sessions, laps, and device_info. Horizontally
 * scrollable with a sticky row-header column, since sessions in
 * particular routinely carry dozens of fields.
 */
export function MessageTable({
  model,
  messagesKey,
  caption,
  rowLabel,
  preferredOrder,
  exclude,
  source,
  timeMode,
  emptyText,
}: MessageTableProps) {
  const messages = model.byKey.get(messagesKey)
  const messageMeta = model.profile?.byMessagesKey.get(messagesKey)
  const columns = useMemo(
    () =>
      discoverColumns(messages ?? [], {
        messageMeta,
        devFields: model.devFields,
        preferredOrder,
        exclude,
      }),
    [messages, messageMeta, model.devFields, preferredOrder, exclude],
  )
  const renderCell = useFieldCell()
  const unitSystem = useUnits()

  if (messages == null || messages.length === 0) {
    return <p className="text-muted-foreground text-sm">{emptyText}</p>
  }

  return (
    <div className="border-border overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-left text-xs">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-muted/60">
          <tr>
            <th
              scope="col"
              className="bg-muted sticky left-0 z-10 px-2 py-1.5 font-semibold whitespace-nowrap"
            >
              {rowLabel}
            </th>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className="px-2 py-1.5 font-semibold whitespace-nowrap"
              >
                <ColumnHeading column={column} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {messages.map((message, index) => (
            <tr key={index} className="border-border border-t">
              <th
                scope="row"
                className="bg-card sticky left-0 z-10 px-2 py-1 font-medium whitespace-nowrap"
              >
                {rowLabel} {index + 1}
              </th>
              {columns.map((column) => {
                const value = readColumn(message, column.id)
                return (
                  <td key={column.id} className="px-2 py-1 whitespace-nowrap tabular-nums">
                    {renderCell(
                      {
                        source,
                        surface: 'messageTable',
                        messagesKey,
                        mesgNum: messageMeta?.num,
                        messageIndex: index,
                        fieldId: column.id,
                        kind: column.kind,
                        value,
                        units: column.units,
                        meta: column.meta,
                      },
                      formatFriendlyValue(column.id, value, column.units, timeMode, {
                        unitSystem,
                      }),
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A column header label plus its undocumented/developer marker. */
export function ColumnHeading({ column }: { column: ColumnDef }) {
  return (
    <span className="inline-flex items-center gap-1">
      {column.label}
      {column.kind !== 'profile' && <FieldKindTag kind={column.kind} />}
    </span>
  )
}

export function FieldKindTag({
  kind,
  className,
}: {
  kind: 'undocumented' | 'developer' | 'profile'
  className?: string
}) {
  if (kind === 'profile') return null
  return (
    <span
      className={cn(
        'rounded border px-1 text-[10px] leading-4 font-medium',
        kind === 'undocumented'
          ? 'border-warning/60 bg-warning/10 text-warning-text'
          : 'border-primary/40 bg-primary/10 text-foreground',
        className,
      )}
    >
      {kind}
    </span>
  )
}
